import React, { MouseEventHandler, useEffect, useRef, useState, VoidFunctionComponent } from "react";
import { ArrowButton } from "@/components/responsive/commun/arrowButton/ArrowButton";
import { ImageSliderItem } from "@/components/responsive/product/imageSliderModal/imageSliderItem/ImageSliderItem";
import { ImageSliderZoom } from "@/components/responsive/product/imageSliderModal/imageSliderZoom/ImageSliderZoom";
import { AdaptiveLoadingResources } from "@/models/scope/productSheet/AdaptiveLoadingResources";
import {
    REGEX_IS_MOBILE_OR_TABLET,
    MD_SLIDER_BREAKPOINT,
    L_SLIDER_BREAKPOINT,
    SLIDER_MAX_IMAGES_TO_DISPLAY,
} from "@/utils/constants";
import Slider, { Settings } from "react-slick";

interface Props {
    medias: string[];
    close: () => void;
    imageToShow: number;
    isMobile: boolean;
    alt: string;
    zoomImages: string[];
    videoUrl?: string;
    videoUrl2?: string;
    positionVideo1?: number;
    positionVideo2?: number;
    adaptiveLoadingResources: AdaptiveLoadingResources;
    isVideoIconDisplayed: (index: number) => boolean;
}

export const ImageSliderModal: VoidFunctionComponent<Props> = ({
    medias,
    close,
    imageToShow,
    isMobile,
    alt,
    zoomImages,
    videoUrl,
    videoUrl2,
    positionVideo1,
    positionVideo2,
    adaptiveLoadingResources,
    isVideoIconDisplayed,
}) => {
    const [isZoom, setIsZoom] = useState(false);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    let imageModalSlider = useRef<Slider>(null);
    const [activeSlide, setActiveSlide] = useState(imageToShow || 0);
    const maxDisplayedImage = medias?.length > SLIDER_MAX_IMAGES_TO_DISPLAY ? SLIDER_MAX_IMAGES_TO_DISPLAY : medias?.length;

    const userAgent = navigator.userAgent;
    const isMobileOrTablet = REGEX_IS_MOBILE_OR_TABLET.test(userAgent);
    const modalSliderSettings: Settings = {
        dotsClass: "slick-dots slick-thumb",
        slidesToShow: 1,
        infinite: false,
        speed: 500,
        slidesToScroll: 1,
        arrows: false,
        lazyLoad: "ondemand",
        initialSlide: activeSlide,
        responsive: [
            {
                breakpoint: MD_SLIDER_BREAKPOINT,
                settings: {
                    dots: true,
                },
            },
            {
                breakpoint: L_SLIDER_BREAKPOINT, // use only to apply following settings on screen with bigger than 728px
                settings: {
                    dots: true,
                    customPaging: function (i: number) {
                        return (
                            <a key={i}>
                                <div className="custom-paging">
                                    <img
                                        src={medias[i]}
                                        className="slider-thumbnail"
                                        loading="lazy"
                                        alt={alt}
                                    />
                                    {isVideoIconDisplayed(i) && (
                                        <img
                                            className="play-button"
                                            src={"/assets/images/responsive/icons/playerIcon.svg"}
                                            alt="play icone"
                                        />
                                    )}
                                </div>
                            </a>
                        );
                    },
                },
            },
        ],
        beforeChange: (currentSlide: number, nextSlide: number): void => {
            setActiveSlide(nextSlide);
        },
    };

    const handleMouseMove: MouseEventHandler = (event) => {
        !isMobileOrTablet && setMousePosition({ x: event.clientX, y: event.clientY });
    };

    const openModal: MouseEventHandler = (event) => {
        setMousePosition({ x: event.clientX, y: event.clientY });
        setIsZoom(true);
    };

    useEffect(() => {
        if (imageModalSlider && imageModalSlider.current && imageModalSlider.current.slickGoTo) {
            imageModalSlider.current.slickGoTo(activeSlide);
        }
    }, [activeSlide]);

    return (
        <div className="slider-modal">
            <div className="slider-modal-content">
                <img
                    onClick={close}
                    src={"/assets/images/responsive/icons/cross.svg"}
                    alt="close"
                    className="close"
                    loading="lazy"
                />
                {isZoom ? (
                    <ImageSliderZoom handleMouseMove={handleMouseMove}
                        setIsZoom={setIsZoom}
                        medias={medias}
                        zoomImages={zoomImages}
                        isMobileOrTablet={isMobileOrTablet}
                        activeSlide={activeSlide}
                        mousePosition={mousePosition}
                    />
                ) : (
                    <div className="slider-modal-block">
                        <ArrowButton
                            onClick={() => imageModalSlider.current?.slickPrev()}
                            isDisable={activeSlide === 0}
                            isRotated={true}
                        />
                        <ArrowButton
                            onClick={() => imageModalSlider.current?.slickNext()}
                            isDisable={activeSlide === maxDisplayedImage - 1}
                            isRotated={false}
                        />
                        <Slider
                            ref={(slider) => (imageModalSlider = {
                                ...imageModalSlider,
                                current: slider
                            })}
                            {...modalSliderSettings}
                        >
                            {medias?.slice(0, SLIDER_MAX_IMAGES_TO_DISPLAY).map((media: string, index: number) => {
                                const urlVideoToDisplay = index == positionVideo1 ? videoUrl : index == positionVideo2 ? videoUrl2 : null;
                                const isVideoPlayed = index == activeSlide && ((!!videoUrl && index == positionVideo1) || (!!videoUrl2 && index == positionVideo2));
                                return (
                                    <ImageSliderItem
                                        openModal={openModal}
                                        isMobile={isMobile}
                                        isVideoPlayed={isVideoPlayed}
                                        videoUrl={urlVideoToDisplay}
                                        adaptiveLoadingResources={adaptiveLoadingResources}
                                        media={media}
                                        alt={alt}
                                    />
                                );
                            })}
                        </Slider>
                    </div>
                )}
            </div>
        </div>
    );
};
